import React from 'react';
import Icon from '../../../components/AppIcon';

const TimelineTracking = ({ auditData }) => {
  const timelineEvents = [
    {
      id: 1,
      date: '2025-11-15',
      time: '09:00 AM',
      event: 'Audit Created',
      description: 'Safety audit survey created with 45 questions from 3 question banks',
      user: 'John Smith',
      status: 'completed',
      icon: 'Plus'
    },
    {
      id: 2,
      date: '2025-11-18',
      time: '02:30 PM',
      event: 'Question Bank Linked',
      description: 'Added Fire Safety and Emergency Procedures question banks',
      user: 'John Smith',
      status: 'completed',
      icon: 'Link'
    },
    {
      id: 3,
      date: '2025-11-20',
      time: '10:00 AM',
      event: 'Audit Published',
      description: 'Survey published to 45 users across Plant 1',
      user: 'John Smith',
      status: 'completed',
      icon: 'Send'
    },
    {
      id: 4,
      date: '2025-11-22',
      time: '03:45 PM',
      event: 'First Responses Received',
      description: '12 users completed the audit within first 48 hours',
      user: 'System',
      status: 'completed',
      icon: 'CheckCircle'
    },
    {
      id: 5,
      date: '2025-11-28',
      time: '11:20 AM',
      event: 'Reminder Sent',
      description: 'Automated reminder sent to 20 users with pending responses',
      user: 'System',
      status: 'completed',
      icon: 'Bell'
    },
    {
      id: 6,
      date: '2025-12-05',
      time: '04:15 PM',
      event: '75% Completion Milestone',
      description: '35 out of 45 users completed the audit',
      user: 'System',
      status: 'completed',
      icon: 'Award'
    },
    {
      id: 7,
      date: '2025-12-15',
      time: '12:00 PM',
      event: 'Second Reminder Scheduled',
      description: 'Follow-up reminder will be sent to remaining 10 users',
      user: 'System',
      status: 'pending',
      icon: 'Clock'
    },
    {
      id: 8,
      date: '2025-12-31',
      time: '11:59 PM',
      event: 'Audit Due Date',
      description: 'Final submission deadline for all participants',
      user: 'System',
      status: 'upcoming',
      icon: 'Calendar'
    }
  ];

  const milestones = [
    { label: 'Created', date: '2025-11-15', status: 'completed', percentage: 0 },
    { label: 'Published', date: '2025-11-20', status: 'completed', percentage: 25 },
    { label: '50% Complete', date: '2025-11-25', status: 'completed', percentage: 50 },
    { label: '75% Complete', date: '2025-12-05', status: 'completed', percentage: 75 },
    { label: 'Due Date', date: '2025-12-31', status: 'upcoming', percentage: 100 }
  ];

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-500',
      pending: 'bg-orange-500',
      upcoming: 'bg-blue-500'
    };
    return colors?.[status] || 'bg-gray-500';
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: 'CheckCircle',
      pending: 'Clock',
      upcoming: 'Calendar'
    };
    return icons?.[status] || 'Circle';
  };

  return (
    <div className="space-y-6">
      {/* Milestone Progress */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Audit Lifecycle Milestones</h3>
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-muted">
            <div 
              className="h-full bg-primary transition-all duration-500"
              style={{ width: '75%' }}
            />
          </div>

          {/* Milestones */}
          <div className="relative flex justify-between">
            {milestones?.map((milestone, index) => (
              <div key={index} className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full ${getStatusColor(milestone?.status)} flex items-center justify-center mb-3 border-4 border-background`}>
                  <Icon name={getStatusIcon(milestone?.status)} size={20} color="white" />
                </div>
                <div className="text-xs font-medium text-foreground text-center mb-1">
                  {milestone?.label}
                </div>
                <div className="text-xs text-muted-foreground text-center">
                  {milestone?.date}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Timeline Events */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-6">Event Timeline</h3>
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

          {/* Events */}
          <div className="space-y-6">
            {timelineEvents?.map((event) => (
              <div key={event?.id} className="relative pl-16">
                <div className={`absolute left-3 w-6 h-6 rounded-full ${getStatusColor(event?.status)} flex items-center justify-center border-4 border-background`}>
                  <Icon name={event?.icon} size={12} color="white" />
                </div>

                <div className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">
                        {event?.event}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {event?.description}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      event?.status === 'completed' ? 'bg-green-500/10 text-green-700' :
                      event?.status === 'pending'? 'bg-orange-500/10 text-orange-700' : 'bg-blue-500/10 text-blue-700'
                    }`}>
                      {event?.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      <span>{event?.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>{event?.time}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      <span>{event?.user}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Completion Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Time to First Response</span>
            <Icon name="Clock" size={16} color="var(--color-primary)" />
          </div>
          <div className="text-2xl font-bold text-foreground">48 hours</div>
          <div className="text-xs text-muted-foreground mt-1">12 responses received</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Average Completion Time</span>
            <Icon name="TrendingUp" size={16} color="var(--color-success)" />
          </div>
          <div className="text-2xl font-bold text-foreground">6.5 days</div>
          <div className="text-xs text-muted-foreground mt-1">From publish to complete</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Days Remaining</span>
            <Icon name="Calendar" size={16} color="var(--color-warning)" />
          </div>
          <div className="text-2xl font-bold text-foreground">26 days</div>
          <div className="text-xs text-muted-foreground mt-1">Until due date</div>
        </div>
      </div>
    </div>
  );
};

export default TimelineTracking;